import {
  saveNotaLocal,
  getNotasLocal,
  getNotaByIdLocal,
  deleteNotaLocal,
  clearAndReplaceNotasLocal,
  getPendingOperations,
  removePendingOperation,
  addPendingOperation,
  saveCategoriesLocal,
  saveCotacoesLocal,
  syncLocalCotacoesToBackend,
  sanitizePendingQueue
} from './db.js';
import { authManager, getSavedScope, setSavedScope } from './auth-manager.js';
import { escapeHTML } from './security.js';
import { openAboutModal } from './views/about-modal.js';
import { openQRModal } from './views/qr-modal.js';
import { openNotificationsModal } from './views/notifications-modal.js';
import { getSavedTheme, setTheme } from './theme-manager.js';

export class SyncManager {
  constructor() {
    this.statusEl = document.getElementById('sync-status');
    this.isOnline = navigator.onLine;
    this._isSyncingQueue = false;
    this._lastResumeCheck = Date.now();

    window.addEventListener('online', () => this.handleOnlineChange(true));
    window.addEventListener('offline', () => this.handleOnlineChange(false));

    // Handle session expiration notification
    window.addEventListener('sessionexpired', (e) => {
      this.updateStatusIndicator();
      this.showToast(e.detail?.message || 'Sessão expirada. Faça login novamente.', 'warning');
    });

    // Handle App Resume (tab re-focus / visibility change from background)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.handleAppResume();
      }
    });
    window.addEventListener('focus', () => {
      this.handleAppResume();
    });

    window.addEventListener('authstatechanged', async () => {
      this.updateStatusIndicator();
      try {
        if (this.isOnline && authManager.isAuthenticated()) {
          await this.pullUpdates();
        }
        window.dispatchEvent(new Event('hashchange'));
      } catch (e) {
        console.warn('Error syncing cache on auth state change:', e);
      }
    });

    this._customPageTitle = null;

    // Sincronizar cabeçalho ao mudar de rota ou de tema/escopo
    window.addEventListener('hashchange', () => {
      this._customPageTitle = null;
      this.updateStatusIndicator();
    });
    window.addEventListener('nfce-scope-changed', () => {
      this.updateStatusIndicator();
    });
    window.addEventListener('nfce-theme-changed', () => {
      this.updateStatusIndicator();
    });
    window.addEventListener('nfce-page-title-changed', (e) => {
      const title = e.detail?.title;
      if (title) {
        this._customPageTitle = title;
        const titleEl = document.getElementById('header-scrolled-title');
        if (titleEl) {
          titleEl.textContent = title;
          titleEl.style.animation = 'none';
          void titleEl.offsetHeight;
          titleEl.style.animation = '';
        }
      }
    });

    this._initScrollHeader();

    const closeUserMenu = () => {
      const dropdown = document.getElementById('user-menu-dropdown');
      if (dropdown) {
        dropdown.classList.remove('open');
        dropdown.classList.remove('show');
      }
    };

    // Fechar menu automaticamente ao navegar por hashchange ou popstate
    window.addEventListener('hashchange', closeUserMenu);
    window.addEventListener('popstate', closeUserMenu);

    // Fechar menu com tecla Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeUserMenu();
    });

    // Global Event Delegation for Header User Menu Actions
    document.addEventListener('click', (e) => {
      const avatarBtn = (e.target.closest('#user-avatar-btn') || e.target.closest('#header-user-text') || e.target.closest('.user-avatar-wrapper')) && !e.target.closest('#user-menu-dropdown');
      if (avatarBtn) {
        e.stopPropagation();
        e.preventDefault();
        const dropdown = document.getElementById('user-menu-dropdown');
        if (dropdown) dropdown.classList.toggle('open');
        return;
      }

      const scopeBtn = e.target.closest('[data-header-scope]');
      if (scopeBtn) {
        e.stopPropagation();
        e.preventDefault();
        const newScope = scopeBtn.getAttribute('data-header-scope');
        if (newScope) {
          setSavedScope(newScope);
        }
        return;
      }

      const bellBtn = e.target.closest('#header-bell-btn');
      if (bellBtn) {
        e.stopPropagation();
        e.preventDefault();
        closeUserMenu();
        openNotificationsModal(this);
        return;
      }

      const qrBtn = e.target.closest('#btn-qr-menu');
      if (qrBtn) {
        e.stopPropagation();
        e.preventDefault();
        closeUserMenu();
        openQRModal();
        return;
      }

      const aboutBtn = e.target.closest('#btn-about-menu');
      if (aboutBtn) {
        e.stopPropagation();
        e.preventDefault();
        closeUserMenu();
        openAboutModal();
        return;
      }

      const manualBtn = e.target.closest('#btn-manual-menu');
      if (manualBtn) {
        e.stopPropagation();
        e.preventDefault();
        closeUserMenu();
        if (window.location.hash === '#manual') {
          window.dispatchEvent(new Event('hashchange'));
        } else {
          window.location.hash = '#manual';
        }
        return;
      }

      const feedbackBtn = e.target.closest('#btn-feedback-menu');
      if (feedbackBtn) {
        e.stopPropagation();
        e.preventDefault();
        closeUserMenu();
        if (window.location.hash === '#feedback') {
          window.dispatchEvent(new Event('hashchange'));
        } else {
          window.location.hash = '#feedback';
        }
        return;
      }

      const adminBtn = e.target.closest('#btn-admin-menu');
      if (adminBtn) {
        e.stopPropagation();
        e.preventDefault();
        closeUserMenu();
        if (window.location.hash === '#adminfeedbacks') {
          window.dispatchEvent(new Event('hashchange'));
        } else {
          window.location.hash = '#adminfeedbacks';
        }
        return;
      }

      const adminLabsBtn = e.target.closest('#btn-admin-labs') || e.target.closest('#btn-admin-product-scanner');
      if (adminLabsBtn) {
        e.stopPropagation();
        e.preventDefault();
        closeUserMenu();
        if (window.location.hash === '#labs') {
          window.dispatchEvent(new Event('hashchange'));
        } else {
          window.location.hash = '#labs';
        }
        return;
      }

      const familyBtn = e.target.closest('#btn-family-menu');
      if (familyBtn) {
        e.stopPropagation();
        e.preventDefault();
        closeUserMenu();
        if (window.location.hash === '#family') {
          window.dispatchEvent(new Event('hashchange'));
        } else {
          window.location.hash = '#family';
        }
        return;
      }

      const productsBtn = e.target.closest('#btn-products-menu');
      if (productsBtn) {
        e.stopPropagation();
        e.preventDefault();
        closeUserMenu();
        if (window.location.hash === '#produtos') {
          window.dispatchEvent(new Event('hashchange'));
        } else {
          window.location.hash = '#produtos';
        }
        return;
      }

      const themeBtn = e.target.closest('.theme-pill-btn');
      if (themeBtn) {
        e.stopPropagation();
        e.preventDefault();
        const themeVal = themeBtn.getAttribute('data-theme-val');
        if (themeVal) {
          setTheme(themeVal);
          const switcher = document.getElementById('theme-switcher-pills');
          if (switcher) {
            switcher.querySelectorAll('.theme-pill-btn').forEach(btn => {
              btn.classList.toggle('active', btn.getAttribute('data-theme-val') === themeVal);
            });
          }
        }
        return;
      }

      const logoutBtn = e.target.closest('#btn-logout-menu');
      if (logoutBtn) {
        e.stopPropagation();
        e.preventDefault();
        closeUserMenu();
        authManager.clearSession();
        return;
      }

      // Fechar menu ao clicar em qualquer item do menu
      const anyMenuItem = e.target.closest('.user-menu-item');
      if (anyMenuItem) {
        closeUserMenu();
      }

      // Close user menu when clicking outside
      const menu = document.getElementById('user-menu-dropdown');
      const avatar = document.getElementById('user-avatar-btn');
      const userText = document.getElementById('header-user-text');
      if (menu && (menu.classList.contains('open') || menu.classList.contains('show')) && !menu.contains(e.target) && avatar && !avatar.contains(e.target) && userText && !userText.contains(e.target)) {
        closeUserMenu();
      }
    });
  }

  _initScrollHeader() {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const appHeader = document.getElementById('app-header');
          if (appHeader) {
            if (window.scrollY > 30) {
              appHeader.classList.add('header-scrolled');
            } else {
              appHeader.classList.remove('header-scrolled');
            }
            document.documentElement.style.setProperty('--app-header-height', `${appHeader.offsetHeight}px`);
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    // Run once at start
    setTimeout(handleScroll, 100);
  }

  _getRouteTitle() {
    if (this._customPageTitle) {
      return this._customPageTitle;
    }
    const hash = window.location.hash || '#home';
    const cleanHash = hash.split('?')[0].split('/')[0];
    const titles = {
      '#home': 'Visão Geral',
      '': 'Visão Geral',
      '#notas': 'Minhas Notas',
      '#listas': 'Minhas Listas',
      '#analise': 'Análise de Gastos',
      '#produtos': 'Catálogo de Produtos',
      '#familia': 'Minha Família',
      '#family': 'Minha Família',
      '#scanner': 'Scanner NFC-e',
      '#scanner-ai': 'Scanner de Produto',
      '#scanner-produto': 'Scanner de Produto',
      '#feedback': 'Enviar Feedback',
      '#manual': 'Manual do App',
      '#guia': 'Manual do App',
      '#admin': 'Painel Admin',
      '#adminfeedbacks': 'Painel Admin',
      '#labs': 'Labs NFC-e',
      '#nota': 'Detalhe da Nota',
      '#detalhe-nota': 'Detalhe da Nota',
      '#lista': 'Detalhe da Lista',
      '#detalhe-lista': 'Detalhe da Lista'
    };
    return titles[cleanHash] || 'NF Tracker';
  }

  _isScopedRoute() {
    const hash = window.location.hash || '#home';
    const cleanHash = hash.split('?')[0].split('/')[0];
    return ['#home', '', '#notas', '#listas', '#analise', '#produtos'].includes(cleanHash);
  }

  async init() {
    this.updateStatusIndicator();
    await sanitizePendingQueue();
    if (this.isOnline) {
      this.syncPendingQueue();
      this.pullUpdates();
      this.fetchFamilyNotifications(false);
    }

    // Polling periodico de notificacoes da familia a cada 25 segundos
    if (!this._notifInterval) {
      this._notifInterval = setInterval(() => {
        if (this.isOnline && authManager.isAuthenticated()) {
          this.fetchFamilyNotifications(true);
        }
      }, 25000);
    }
  }

  handleOnlineChange(online) {
    this.isOnline = online;
    this.updateStatusIndicator();
    if (online) {
      this.showToast('Conexão restabelecida. Sincronizando...', 'info');
      this.syncPendingQueue();
      this.pullUpdates();
      this.fetchFamilyNotifications(false);
    } else {
      this.showToast('Você está offline. Alterações salvas localmente.', 'info');
    }
  }

  updateNotificationBadge(unreadCount) {
    const bellBtn = document.getElementById('header-bell-btn');
    if (!bellBtn) return;

    let badgeEl = bellBtn.querySelector('.bell-badge-count');
    const dotEl = bellBtn.querySelector('.home-badge-dot');

    if (unreadCount > 0) {
      if (dotEl) dotEl.style.display = 'none';
      if (!badgeEl) {
        badgeEl = document.createElement('span');
        badgeEl.className = 'bell-badge-count';
        bellBtn.appendChild(badgeEl);
      }
      badgeEl.textContent = unreadCount > 9 ? '9+' : unreadCount;
      badgeEl.style.display = 'flex';
      bellBtn.classList.add('has-unread');
    } else {
      if (badgeEl) badgeEl.remove();
      if (dotEl) dotEl.style.display = 'none';
      bellBtn.classList.remove('has-unread');
    }
  }

  async fetchFamilyNotifications(vibrateIfNew = false) {
    if (!this.isOnline || !authManager.isAuthenticated()) return;
    try {
      const res = await authManager.fetchWithAuth('/api/family/notifications?limit=25');
      if (!res.ok) return;
      const data = await res.json();
      const notifs = data.notifications || [];
      const lastSeen = localStorage.getItem('nfce_last_seen_notif_time') || '1970-01-01T00:00:00Z';

      const unread = notifs.filter(n => n.createdAt > lastSeen);
      const unreadCount = unread.length;

      const prevUnreadCount = parseInt(localStorage.getItem('nfce_last_unread_count') || '0', 10);
      localStorage.setItem('nfce_last_unread_count', String(unreadCount));

      if (vibrateIfNew && unreadCount > prevUnreadCount && unreadCount > 0) {
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
          try {
            navigator.vibrate([100, 50, 100]);
          } catch (_) {}
        }
      }

      this.updateNotificationBadge(unreadCount);
    } catch (e) {
      // Silent error handling for background notifications
    }
  }

  updateStatusIndicator() {
    const userSection = document.getElementById('header-user-section');
    const actionsSection = document.getElementById('header-actions-section');
    const user = authManager.getUser();

    // Only display top header content when logged in
    if (!user || !authManager.isAuthenticated()) {
      if (userSection) userSection.innerHTML = '';
      if (actionsSection) actionsSection.innerHTML = '';
      return;
    }

    const name = escapeHTML(user.name || 'Usuário');
    const currentScope = getSavedScope();
    const currentTheme = getSavedTheme();
    const routeTitle = this._getRouteTitle();
    const isNext = typeof window !== 'undefined' && (
      window.location.pathname.startsWith('/next') ||
      localStorage.getItem('nfce_app_channel') === 'next' ||
      (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.BASE_URL && import.meta.env.BASE_URL.includes('next'))
    );

    const nextBadgeHtml = isNext
      ? `<span class="header-next-badge" title="Canal Next (Versão Experimental / Preview)">NEXT</span>`
      : '';

    // Render Left Section (Avatar + User Name/Greeting + Scrolled Route Title)
    if (userSection) {
      userSection.innerHTML = `
        <div class="user-avatar-wrapper">
          ${this._renderUserAvatar(user)}
        </div>
        <div class="header-user-text" id="header-user-text" title="Ver menu do perfil">
          <span class="header-user-greeting">Bem-vindo de volta,</span>
          <h2 class="header-user-name">
            <span>${name}</span>
            ${nextBadgeHtml}
          </h2>
        </div>
        <div class="header-title-scrolled" id="header-title-scrolled">
          <h2 class="header-scrolled-title" id="header-scrolled-title">${routeTitle}</h2>
          ${nextBadgeHtml}
        </div>
      `;
    }

    // Render Right Section (Scope Switcher + Quick Theme Toggle + Sync + Notifications)
    if (actionsSection) {
      const syncClass = this._syncState === 'error'
        ? 'sync-error'
        : (this._syncState === 'syncing' ? 'sync-spinning' : (this.isOnline ? 'sync-online' : 'sync-offline'));
      const isSpinning = this._syncState === 'syncing' || this._isSyncingQueue;

      actionsSection.innerHTML = `
        ${this._isScopedRoute() ? `
          <div class="header-scope-pills" id="header-scope-pills">
            <button type="button" class="header-scope-pill ${currentScope === 'personal' ? 'active' : ''}" data-header-scope="personal" title="Meus Gastos (Pessoal)">
              <span class="material-symbols-outlined" style="font-size: 15px;">person</span>
              <span>Pessoal</span>
            </button>
            <button type="button" class="header-scope-pill ${currentScope === 'family' ? 'active' : ''}" data-header-scope="family" title="Minha Família (Compartilhado)">
              <span class="material-symbols-outlined" style="font-size: 15px;">group</span>
              <span>Família</span>
            </button>
          </div>
        ` : ''}

        <button class="header-icon-btn ${syncClass}" id="btn-force-sync" title="${this.isOnline ? 'Conectado ao Servidor (Clique para sincronizar)' : 'Modo Offline (dados salvos localmente)'}">
          <span class="material-symbols-outlined header-sync-icon ${isSpinning ? 'spinning' : ''}">sync</span>
        </button>

        <button class="header-icon-btn" id="header-bell-btn" title="Acesso Mobile / Notificações">
          <span class="material-symbols-outlined">notifications</span>
          <span class="home-badge-dot"></span>
        </button>
      `;

      // Bind sync button
      const btnSync = actionsSection.querySelector('#btn-force-sync');
      if (btnSync) {
        btnSync.addEventListener('click', (e) => {
          e.stopPropagation();
          this.forceFullSync();
        });
      }
    }
  }

  _renderUserAvatar(user) {
    const name = escapeHTML(user.name || 'Usuário');
    const email = escapeHTML(user.email || '');
    const picture = (user.picture || '').trim();
    const initials = (user.name || 'U').split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || 'U';

    const avatarContent = `
      <div class="user-avatar-content" style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; position: relative; border-radius: 50%; overflow: hidden;">
        ${picture ? `<img src="${escapeHTML(picture)}" alt="" referrerpolicy="no-referrer" loading="lazy" style="width: 100%; height: 100%; object-fit: cover; border-radius: 50%;" onerror="this.style.display='none'; if(this.nextElementSibling) this.nextElementSibling.style.display='flex';" />` : ''}
        <span style="${picture ? 'display: none;' : 'display: flex;'} width: 100%; height: 100%; align-items: center; justify-content: center; font-size: 0.88rem; font-weight: 800; color: #1E293B; background: linear-gradient(135deg, #E2E8F0, #CBD5E1);">${initials}</span>
      </div>
    `;

    return `
      <button class="user-avatar-btn" id="user-avatar-btn" title="${name}">
        ${avatarContent}
      </button>
      <div class="user-menu-dropdown" id="user-menu-dropdown">
        <!-- Logo / Brand inside User Menu Header -->
        <div class="user-menu-brand">
          <div class="user-menu-brand-left">
            <img src="/favicon.svg" alt="NF Tracker Logo" style="width: 22px; height: 22px; border-radius: 6px;" />
            <span class="app-logo-text">NF Tracker</span>
          </div>
          <span class="badge-beta">Beta</span>
        </div>
        <div class="user-menu-divider"></div>

        <div class="user-menu-header">
          <div class="user-menu-avatar">
            ${avatarContent}
          </div>
          <div class="user-menu-info">
            <div class="user-menu-name">${name}</div>
            <div class="user-menu-email">${email}</div>
          </div>
        </div>
        <div class="user-menu-divider"></div>
        <button class="user-menu-item" id="btn-products-menu">
          <span class="material-symbols-outlined">inventory_2</span>
          Catálogo de Produtos
        </button>
        <button class="user-menu-item" id="btn-family-menu">
          <span class="material-symbols-outlined">group</span>
          Minha Família
        </button>
        <button class="user-menu-item qr-menu-item" id="btn-qr-menu">
          <span class="material-symbols-outlined">qr_code_2</span>
          Acesso Mobile (QR Code)
        </button>
        <button class="user-menu-item" id="btn-feedback-menu">
          <span class="material-symbols-outlined">rate_review</span>
          Enviar Feedback
        </button>
        <button class="user-menu-item" id="btn-manual-menu">
          <span class="material-symbols-outlined">menu_book</span>
          Guia &amp; Manual do App
        </button>
        <button class="user-menu-item" id="btn-about-menu">
          <span class="material-symbols-outlined">info</span>
          Sobre o App
        </button>
        ${user.isAdmin ? `
          <div class="user-menu-divider"></div>
          <button class="user-menu-item" id="btn-admin-menu">
            <span class="material-symbols-outlined">admin_panel_settings</span>
            Painel Administrador
          </button>
          <button class="user-menu-item" id="btn-admin-labs">
            <span class="material-symbols-outlined">science</span>
            Labs (Testes & PoCs)
          </button>
        ` : ''}
        <!-- Theme Switcher (Dia / Noite) -->
        <div class="user-menu-divider"></div>
        <div class="user-menu-theme-row">
          <div class="user-menu-theme-label">
            <span class="material-symbols-outlined" style="font-size: 1.15rem; color: var(--text-muted);">contrast</span>
            <span>Aparência</span>
          </div>
          <div class="theme-switcher-pills" id="theme-switcher-pills">
            <button class="theme-pill-btn ${getSavedTheme() === 'light' ? 'active' : ''}" data-theme-val="light" title="Tema Claro (Dia)">
              <span class="material-symbols-outlined" style="font-size: 1rem;">light_mode</span>
              <span>Dia</span>
            </button>
            <button class="theme-pill-btn ${getSavedTheme() === 'dark' ? 'active' : ''}" data-theme-val="dark" title="Tema Escuro (Noite)">
              <span class="material-symbols-outlined" style="font-size: 1rem;">dark_mode</span>
              <span>Noite</span>
            </button>
          </div>
        </div>
        <div class="user-menu-divider"></div>
        <button class="user-menu-item" id="btn-logout-menu" style="color: #EF4444;">
          <span class="material-symbols-outlined" style="color: #EF4444;">logout</span>
          Sair da Conta
        </button>
      </div>
    `;
  }

  async parseAndSyncNFCe(url, options = {}) {
    if (!this.isOnline) {
      // Offline mode: store URL in pending queue
      const offlineNota = {
        id: Date.now(),
        url,
        status: 'pendente',
        dataEmissao: new Date().toISOString(),
        emitente: { razaoSocial: 'Aguardando sincronização' },
        totais: { valorTotal: 0, qtdItens: 0 },
        syncStatus: 'pending'
      };
      await saveNotaLocal(offlineNota);
      if (!options.fromQueue) {
        await addPendingOperation({ type: 'PARSE_NFCE', url });
      }
      if (!options.silent) {
        this.showToast('Nota salva offline. Será processada ao conectar.', 'info');
      }
      return offlineNota;
    }

    try {
      const response = await fetch('/api/parse-nfce', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authManager.getToken()}`
        },
        body: JSON.stringify({ url })
      });

      if (response.status === 409) {
        const existing = await response.json();
        if (!options.silent) {
          this.showToast('Esta nota já foi escaneada anteriormente.', 'warning');
        }
        return existing;
      }

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        const isClientError = response.status >= 400 && response.status < 500 && response.status !== 408 && response.status !== 429;
        const error = new Error(err.error || 'Erro ao processar NFCe');
        error.isClientError = isClientError;
        error.status = response.status;
        throw error;
      }

      const nota = await response.json();
      await saveNotaLocal(nota);
      if (!options.silent) {
        this.showToast('NFCe processada com sucesso!', 'success');
      }
      return nota;
    } catch (err) {
      console.error('Scan error:', err);
      // Fallback save to pending queue ONLY IF this is a new scan from user (not from queue) and NOT a permanent client error
      if (!options.fromQueue && !err.isClientError) {
        await addPendingOperation({ type: 'PARSE_NFCE', url });
      }
      throw err;
    }
  }

  async handleAppResume() {
    const now = Date.now();
    if (this._lastResumeCheck && (now - this._lastResumeCheck < 6000)) {
      return;
    }
    this._lastResumeCheck = now;

    if (!this.isOnline || !authManager.isAuthenticated()) {
      return;
    }

    try {
      const isValid = await authManager.verifySession();
      if (!isValid) {
        return;
      }

      await this.syncPendingQueue();
      await this.pullUpdates();
      window.dispatchEvent(new CustomEvent('datasyncupdated'));
    } catch (err) {
      console.warn('handleAppResume sync error:', err);
    }
  }

  async pullUpdates() {
    try {
      if (!authManager.isAuthenticated()) return;
      const scope = getSavedScope();
      const res = await authManager.fetchWithAuth(`/api/notas?scope=${scope}&full=true`);
      if (!res.ok) return;
      const remoteNotas = await res.json();

      await clearAndReplaceNotasLocal(remoteNotas);

      // Fetch categories
      const catRes = await authManager.fetchWithAuth('/api/categorias');
      if (catRes.ok) {
        const cats = await catRes.json();
        await saveCategoriesLocal(cats);
      }

      // Sync price quotes bidirectionally across devices
      try {
        // 1. Push any quotes created locally (e.g. on mobile while offline/manual)
        await syncLocalCotacoesToBackend();

        // 2. Pull recent price quotes from backend into local IndexedDB
        const cotRes = await authManager.fetchWithAuth('/api/cotacoes/recentes?limit=150');
        if (cotRes.ok) {
          const cotData = await cotRes.json();
          if (Array.isArray(cotData.cotacoes) && cotData.cotacoes.length > 0) {
            await saveCotacoesLocal(cotData.cotacoes);
          }
        }
      } catch (e) {
        console.warn('Sync cotacoes error:', e);
      }

      await this.fetchFamilyNotifications(true);
    } catch (err) {
      console.warn('Pull sync failed:', err);
    }
  }

  _setSyncButtonState(state) {
    this._syncState = state;
    const btn = document.getElementById('btn-force-sync');
    if (!btn) return;
    const icon = btn.querySelector('.header-sync-icon');

    btn.classList.remove('sync-spinning', 'sync-success', 'sync-error', 'sync-offline', 'sync-online');
    if (icon) icon.classList.remove('spinning');

    if (state === 'syncing') {
      btn.classList.add('sync-spinning');
      if (icon) icon.classList.add('spinning');
      btn.setAttribute('title', 'Sincronizando com o servidor...');
    } else if (state === 'success') {
      btn.classList.add('sync-online', 'sync-success');
      btn.setAttribute('title', 'Sincronizado com sucesso (Conectado)');
    } else if (state === 'error') {
      btn.classList.add('sync-error');
      btn.setAttribute('title', 'Falha ao sincronizar com o servidor');
    } else if (state === 'offline') {
      btn.classList.add('sync-offline');
      btn.setAttribute('title', 'Modo Offline (dados salvos localmente)');
    } else {
      btn.classList.add(this.isOnline ? 'sync-online' : 'sync-offline');
    }
  }

  async forceFullSync() {
    if (!this.isOnline) {
      this._setSyncButtonState('error');
      return;
    }

    try {
      this._setSyncButtonState('syncing');
      const isValid = await authManager.verifySession();
      if (!isValid) {
        this._setSyncButtonState('error');
        return;
      }

      await this.syncPendingQueue();

      const scope = getSavedScope();
      const res = await authManager.fetchWithAuth(`/api/notas?scope=${scope}&full=true`);
      if (!res.ok) throw new Error('Servidor indisponível');
      const remoteNotas = await res.json();

      await clearAndReplaceNotasLocal(remoteNotas);

      const catRes = await authManager.fetchWithAuth('/api/categorias');
      if (catRes.ok) {
        const cats = await catRes.json();
        await saveCategoriesLocal(cats);
      }

      await this.fetchFamilyNotifications(true);

      // Sucesso: para de girar e permanece verde
      this._setSyncButtonState('success');
      window.dispatchEvent(new Event('hashchange'));
    } catch (err) {
      console.error('Force sync error:', err);
      // Erro: para de girar e fica em tom vermelho
      this._setSyncButtonState('error');
    }
  }

  async syncPendingQueue() {
    if (this._isSyncingQueue) return;
    this._isSyncingQueue = true;

    try {
      await sanitizePendingQueue();
      const queue = await getPendingOperations();
      if (!queue || queue.length === 0) return;

      for (const op of queue) {
        try {
          if (op.type === 'PARSE_NFCE') {
            await this.parseAndSyncNFCe(op.url, { silent: true, fromQueue: true });
          }
          await removePendingOperation(op.id);
        } catch (err) {
          console.error('Failed to sync pending op:', op, err);
          // If fatal client error (400, 422, etc.) or retries exhausted, discard it from queue
          if (err.isClientError || (op.retries && op.retries >= 2)) {
            await removePendingOperation(op.id);
          }
        }
      }
    } catch (e) {
      console.warn('syncPendingQueue global error:', e);
    } finally {
      this._isSyncingQueue = false;
    }
  }

  showToast(message, type = 'info') {
    const container = document.getElementById('toast-container');
    if (!container) return;

    // Deduplicate: remove any existing toast with the same message or same status type
    const existingToasts = Array.from(container.children);
    existingToasts.forEach(t => {
      if (t.textContent && (t.textContent.includes(message) || t.classList.contains(`toast-${type}`))) {
        t.remove();
      }
    });

    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.innerHTML = `
      <span>${type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'}</span>
      <span>${escapeHTML(message)}</span>
    `;

    container.appendChild(toast);
    setTimeout(() => {
      toast.style.animation = 'toastOut 250ms forwards';
      setTimeout(() => toast.remove(), 250);
    }, 4000);
  }
}
